<script setup lang="ts">
import { h, ref, onMounted, computed, type VNode } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { NIcon, useDialog, type DropdownOption } from 'naive-ui';
import type { WordApi } from '@/office/word/wordTypes';
import {
  IconCalendar,
  IconCompare,
  IconDelete,
  IconDocumentText,
  IconDownload,
  IconEdit,
  IconEye,
  IconMore,
  IconPaste,
  IconTime,
} from '@/components/design/icons';
import Layout from '@/components/design/layout/Layout.vue';
import GaiaButton from '@/components/design/base/GaiaButton.vue';
import GaiaUploadButton from '@/components/design/base/GaiaUploadButton.vue';
import GaiaDrawer from '@/components/design/base/GaiaDrawer.vue';
import GaiaDropdown from '@/components/design/base/GaiaDropdown.vue';
import GaiaFormField from '@/components/design/base/GaiaFormField.vue';
import GaiaHeader from '@/components/design/base/GaiaHeader.vue';
import GaiaIconButton from '@/components/design/base/GaiaIconButton.vue';
import GaiaInput from '@/components/design/base/inputs/GaiaInput.vue';
import GaiaModal from '@/components/design/base/GaiaModal.vue';
import GaiaTag from '@/components/design/base/GaiaTag.vue';
import GaiaText from '@/components/design/base/GaiaText.vue';
import GaiaTextarea from '@/components/design/base/inputs/GaiaTextarea.vue';
import GaiaTooltip from '@/components/design/base/GaiaTooltip.vue';
import GaiaDataTable, {
  type GaiaDataColumn,
} from '@/components/design/composite/GaiaDataTable.vue';
import GaiaEmptyState from '@/components/design/composite/GaiaEmptyState.vue';
import GaiaNotFound from '@/components/design/composite/GaiaNotFound.vue';
import { navLabels } from '@/components/design/domain/navigation/navLabels';
import GaiaTableSkeleton from '@/components/design/composite/GaiaTableSkeleton.vue';
import GaiaPagination from '@/components/design/composite/GaiaPagination.vue';
import GaiaTableRowState from '@/components/design/composite/GaiaTableRowState.vue';
import {
  gaiaAccent,
  gaiaContentWidth,
  gaiaDiff,
  gaiaFonts,
  gaiaFontSize,
  gaiaFontWeight,
  gaiaNeutral,
  gaiaStatus,
} from '@/components/design/themes/tokens';
import {
  getVaultFileMetadata,
  PreprocessingTaskStatus,
  VaultFileRevisionKind,
  type VaultFileRevisionInDb,
  type VaultFileInDbOwner,
} from '@/api';
import { useVaultStore } from '@/stores/vaultStore';
import VaultFileRevisionUploadModal from '@/components/file_vault/VaultFileRevisionUploadModal.vue';
import GaiaVaultCrumbs from '@/components/design/domain/fileVault/GaiaVaultCrumbs.vue';
import GaiaVaultFilePdfPreview from '@/components/design/domain/fileVault/GaiaVaultFilePdfPreview.vue';
import VaultFileRevisionCompareModal from '@/components/file_vault/VaultFileRevisionCompareModal.vue';
import VaultFileRevisionDiffView from '@/components/file_vault/VaultFileRevisionDiffView.vue';
import RevisionSignatureCell from '@/components/file_vault/RevisionSignatureCell.vue';
import { isOfficeAddinHost } from '@/office/utils';
import { logger } from '@/utils/logger';
import { extractErrorDetail, isAccessDenied } from '@/utils/errorHandler';
import {
  ACCESS_PERMISSION_ADMIN,
  ACCESS_PERMISSION_WRITE,
  deniedReason,
} from '@/utils/accessPermission';
import { useGaiaToast } from '@/composables/useGaiaToast';

const route = useRoute();
const router = useRouter();
const vaultStore = useVaultStore();
const message = useGaiaToast();
const dialog = useDialog();

const uid = route.params.uid as string;

const fileMetadata = ref<VaultFileInDbOwner | null>(null);
const revisions = ref<VaultFileRevisionInDb[]>([]);
const totalCount = ref(0);
const currentPage = ref(1);
const pageSize = ref(20);
const loading = ref(false);
/** The backend refused the file: deleted, no access, or another organisation's. */
const unavailable = ref(false);
const showUploadModal = ref(false);
const showCompareModal = ref(false);
const showDiffView = ref(false);
const compareRevisionA = ref<VaultFileRevisionInDb | null>(null);
const compareRevisionB = ref<VaultFileRevisionInDb | null>(null);

// Preview drawer state
const showPreviewDrawer = ref(false);
const previewRevisionFileUid = ref<string | null>(null);
const previewRevisionUid = ref<string | null>(null);
const previewRevisionStatus = ref<string | null>(null);

// Download state
const downloadingRevisionUid = ref<string | null>(null);

// Edit modal state
const showEditModal = ref(false);
const editingRevision = ref<VaultFileRevisionInDb | null>(null);
const editFileName = ref('');
const editDescription = ref('');
const editSaving = ref(false);

// Access control: the caller's bits on the file (null, not computed, gates nothing).
const writeBlock = computed(() =>
  deniedReason(fileMetadata.value?.permissions, ACCESS_PERMISSION_WRITE)
);
const adminBlock = computed(() =>
  deniedReason(fileMetadata.value?.permissions, ACCESS_PERMISSION_ADMIN)
);
const isOfficeAddin = computed(() => isOfficeAddinHost());
// The task pane is ~320px wide and the office shell scrolls the page itself, so
// there the layout is static with its own tight gutters and no breadcrumb bar.
const layoutProps = isOfficeAddin.value
  ? { contentMaxWidth: '100%', contentPadding: '12px 12px 24px', position: 'static' as const }
  : { contentMaxWidth: gaiaContentWidth.table };

const fileName = computed(() => fileMetadata.value?.current_revision?.file_name ?? '');

function formatBytes(bytes: number | null | undefined): string {
  // A draft's revision stores no file (it is rendered on read), so it has no size.
  if (bytes == null) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 ** 2) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 ** 2).toFixed(1)} MB`;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString();
}

type OfficeRevisionActionKey = 'preview' | 'insert-into-word' | 'download' | 'edit' | 'delete';

function createDropdownIcon(icon: Record<string, unknown>): () => VNode {
  return () => h(NIcon, { size: 16 }, { default: () => h(icon) });
}

function getCompactActionsMenuProps(): { class: string } {
  return {
    class: 'file-vault-compact-actions-menu',
  };
}

function isDocxRevision(revision: VaultFileRevisionInDb): boolean {
  return (
    revision.mime_type ===
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
    /\.docx$/i.test(revision.file_name)
  );
}

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (): void => {
      const result = reader.result;
      if (typeof result !== 'string') {
        reject(new Error('Failed to convert file to base64.'));
        return;
      }

      const commaIndex = result.indexOf(',');
      resolve(commaIndex === -1 ? result : result.slice(commaIndex + 1));
    };
    reader.onerror = (): void => {
      reject(reader.error ?? new Error('Failed to read file.'));
    };
    reader.readAsDataURL(blob);
  });
}

async function loadData(): Promise<void> {
  loading.value = true;
  const offset = (currentPage.value - 1) * pageSize.value;
  try {
    const [metaRes, revisionsRes] = await Promise.all([
      getVaultFileMetadata({ path: { file_uid: uid } }),
      vaultStore.listRevisions(uid, pageSize.value, offset),
    ]);
    fileMetadata.value = metaRes.data ?? null;
    revisions.value = revisionsRes.results;
    totalCount.value = revisionsRes.result_count;
  } catch (error) {
    if (isAccessDenied(error)) {
      // An answer, not a failure: the page renders "not available" instead.
      logger.warn('Vault file not available:', error);
      unavailable.value = true;
      return;
    }
    message.error('Failed to load file revisions.');
  } finally {
    loading.value = false;
  }
}

function handlePageChange(page: number): void {
  currentPage.value = page;
  void loadData();
}

function handlePageSizeChange(size: number): void {
  pageSize.value = size;
  currentPage.value = 1;
  void loadData();
}

function handleCompare({
  revisionA,
  revisionB,
}: {
  revisionA: VaultFileRevisionInDb;
  revisionB: VaultFileRevisionInDb;
}): void {
  compareRevisionA.value = revisionA;
  compareRevisionB.value = revisionB;
  showCompareModal.value = false;
  showDiffView.value = true;
}

async function handleDelete(revision: VaultFileRevisionInDb): Promise<void> {
  try {
    await vaultStore.deleteRevision(uid, revision.uid);
    message.success(`Revision v${revision.version_number} deleted.`);
    await loadData();
  } catch {
    message.error('Failed to delete revision.');
  }
}

async function handleDownload(revision: VaultFileRevisionInDb): Promise<void> {
  downloadingRevisionUid.value = revision.uid;
  try {
    const blob = await vaultStore.downloadFile(revision.uid, revision.mime_type);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = revision.file_name;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }, 100);
  } catch (error) {
    message.error(
      extractErrorDetail(error, `Failed to download revision v${revision.version_number}.`)
    );
  } finally {
    downloadingRevisionUid.value = null;
  }
}

function openPreview(revision: VaultFileRevisionInDb): void {
  if (revision.preview_status !== PreprocessingTaskStatus.DONE) return;
  previewRevisionFileUid.value = uid;
  previewRevisionUid.value = revision.uid;
  previewRevisionStatus.value = revision.preview_status ?? null;
  showPreviewDrawer.value = true;
}

async function insertRevisionIntoCurrentWordDocument(
  revision: VaultFileRevisionInDb
): Promise<void> {
  const word = (window as Window & { Word?: WordApi }).Word;

  if (!word?.run) {
    message.error('Word document context is not available.');
    return;
  }

  if (!isDocxRevision(revision)) {
    message.error('Only .docx files can be inserted into the current Word document.');
    return;
  }

  try {
    loading.value = true;
    const blob = await vaultStore.downloadFile(
      revision.uid,
      revision.mime_type ||
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    );
    const base64File = await blobToBase64(blob);

    await word.run(async context => {
      const selection = context.document.getSelection();
      const supportsHiddenDocument = Boolean(
        window.Office?.context?.requirements?.isSetSupported?.('WordApiHiddenDocument', '1.3')
      );

      if (supportsHiddenDocument && context.application?.createDocument) {
        const externalDoc = context.application.createDocument(base64File);
        await context.sync();

        const externalBody = externalDoc.body;
        const externalBodyOoxml = externalBody.getOoxml();
        await context.sync();

        selection.insertOoxml(externalBodyOoxml.value, 'Replace');
        await context.sync();
        return;
      }

      selection.insertFileFromBase64(base64File, 'Start');
      await context.sync();
    });

    message.success(`Inserted ${revision.file_name} into the current Word document.`);
  } catch (error) {
    logger.error('Failed to insert revision into current Word document:', error);
    const detailedMessage =
      error && typeof error === 'object' && 'message' in error
        ? String(error.message)
        : 'Failed to insert the selected version into the current Word document.';
    message.error(detailedMessage);
  } finally {
    loading.value = false;
  }
}

function handleInsertIntoWord(revision: VaultFileRevisionInDb): void {
  dialog.warning({
    title: 'Insert version into Word',
    content: 'This will insert the selected .docx version at the current cursor position in Word.',
    positiveText: 'Insert',
    negativeText: 'Cancel',
    onPositiveClick: () => insertRevisionIntoCurrentWordDocument(revision),
  });
}

function getOfficeRevisionActionOptions(revision: VaultFileRevisionInDb): DropdownOption[] {
  const options: DropdownOption[] = [];

  if (isDocxRevision(revision)) {
    options.push({
      key: 'insert-into-word',
      label: 'Insert into current document',
      icon: createDropdownIcon(IconPaste),
    });
  }

  options.push(
    {
      key: 'download',
      label: 'Download',
      icon: createDropdownIcon(IconDownload),
    },
    {
      key: 'edit',
      label: 'Edit',
      icon: createDropdownIcon(IconEdit),
      disabled: writeBlock.value !== null,
      props: { title: writeBlock.value ?? undefined },
    },
    {
      key: 'delete',
      label: 'Delete',
      icon: createDropdownIcon(IconDelete),
      disabled: deleteBlockedReason.value !== undefined,
      props: { title: deleteBlockedReason.value },
    }
  );

  return options;
}

async function handleOfficeRevisionActionSelect(
  key: string | number,
  revision: VaultFileRevisionInDb
): Promise<void> {
  const action = String(key) as OfficeRevisionActionKey;

  switch (action) {
    case 'preview':
      openPreview(revision);
      return;
    case 'insert-into-word':
      handleInsertIntoWord(revision);
      return;
    case 'download':
      await handleDownload(revision);
      return;
    case 'edit':
      openEditModal(revision);
      return;
    case 'delete':
      if (totalCount.value === 1) return;
      dialog.warning({
        title: `Delete revision v${revision.version_number}?`,
        content: 'This cannot be undone.',
        positiveText: 'Delete',
        negativeText: 'Cancel',
        positiveButtonProps: { type: 'error' },
        onPositiveClick: () => handleDelete(revision),
      });
      return;
    default:
      return;
  }
}

function openEditModal(revision: VaultFileRevisionInDb): void {
  editingRevision.value = revision;
  editFileName.value = revision.file_name;
  editDescription.value = revision.change_description ?? '';
  showEditModal.value = true;
}

function closeEditModal(): void {
  if (editSaving.value) return;
  showEditModal.value = false;
  editingRevision.value = null;
}

async function handleEditSave(): Promise<void> {
  if (!editingRevision.value || editSaving.value) return;
  editSaving.value = true;
  try {
    await vaultStore.updateRevision(uid, editingRevision.value.uid, {
      file_name: editFileName.value.trim() || null,
      change_description: editDescription.value.trim() || null,
    });
    message.success(`Revision v${editingRevision.value.version_number} updated.`);
    showEditModal.value = false;
    editingRevision.value = null;
    await loadData();
  } catch {
    message.error('Failed to update revision.');
  } finally {
    editSaving.value = false;
  }
}

/**
 * The newest revision, which the list marks with a dot. The server orders by
 * version number, but "current" is the file's own pointer — read it from the
 * file rather than assuming the first row on page one.
 */
const currentRevisionUid = computed(() => fileMetadata.value?.current_revision?.uid ?? null);

/** A draft: its current revision is rendered from a template; the wizard edits it. */
const isDraft = computed(
  () => fileMetadata.value?.current_revision?.kind === VaultFileRevisionKind.TEMPLATED
);

function continueEditing(): void {
  void router.push({ name: 'contract-generation-draft', params: { draftUid: uid } });
}

/** The row whose delete was asked for; its strip replaces the row until answered. */
const confirmRevisionUid = ref<string | null>(null);

/**
 * Deleting a revision needs ADMIN on the file, and deleting the last one would
 * leave the file with no content, which the backend rejects — so the control
 * says why instead of failing on click.
 */
const deleteBlockedReason = computed(
  () =>
    adminBlock.value ??
    (totalCount.value === 1 ? 'A file must keep at least one version' : undefined)
);

function askDelete(revision: VaultFileRevisionInDb): void {
  if (deleteBlockedReason.value) return;
  confirmRevisionUid.value = revision.uid;
}

async function confirmDelete(revision: VaultFileRevisionInDb): Promise<void> {
  confirmRevisionUid.value = null;
  await handleDelete(revision);
}

function canPreview(revision: VaultFileRevisionInDb): boolean {
  return revision.preview_status === PreprocessingTaskStatus.DONE;
}

/**
 * The Word task pane is a third of this width, so it drops the two prose
 * columns rather than letting every other one crush.
 */
const tableColumns = computed<GaiaDataColumn[]>(() => {
  const base: GaiaDataColumn[] = [
    { key: 'version_number', label: 'Version', width: '86px' },
    { key: 'file_name', label: 'File name', width: 'minmax(180px,1fr)' },
    { key: 'size_b', label: 'Size', width: '100px' },
    { key: 'signatures', label: 'Signatures', width: '140px' },
  ];
  if (!isOfficeAddin.value) {
    base.push(
      { key: 'change_description', label: 'Description', width: 'minmax(140px,1fr)' },
      { key: 'created_at', label: 'Uploaded at', width: '150px' }
    );
  }
  return base;
});

const skeletonCells = computed(() =>
  isOfficeAddin.value
    ? ['38px', '64%', '46px', '54px']
    : ['38px', '64%', '46px', '54px', '70%', '96px']
);

onMounted(loadData);

const accentColor = gaiaAccent.base;
const markSurface = gaiaAccent.wash;
const subtleColor = gaiaNeutral.textSubtle;
const faintColor = gaiaNeutral.textFaint;
const softColor = gaiaNeutral.textSoft;
const textColor = gaiaNeutral.text;
const pillBorder = gaiaNeutral.border;
const sizeSurface = gaiaNeutral.surfaceTint;
const rowBorder = gaiaNeutral.borderSoft;
const rowHover = gaiaNeutral.surfaceSoft;
const alertColor = gaiaStatus.alert;
const alertHover = gaiaStatus.alertHover;
const currentColor = gaiaDiff.addedStrong;
const monoFont = gaiaFonts.mono;
const pillSize = gaiaFontSize.micro;
const pillWeight = String(gaiaFontWeight.semibold);
</script>

<template>
  <Layout v-bind="layoutProps">
    <template v-if="!isOfficeAddin" #breadcrumb>
      <n-breadcrumb-item v-if="unavailable">{{ navLabels.fileVault }}</n-breadcrumb-item>
      <template v-else>
        <!-- A contract's file: the trail ends with its contract. -->
        <gaia-vault-crumbs kind="file" :uid="uid" />
        <n-breadcrumb-item>{{ fileName || '—' }}</n-breadcrumb-item>
      </template>
    </template>
    <!-- The file is gone, or not visible from the active organisation. -->
    <gaia-not-found v-if="unavailable" />
    <n-flex v-else vertical :size="18" class="versions-page" :class="{ office: isOfficeAddin }">
      <!-- The task pane has no breadcrumb bar, so it keeps its own way back. -->
      <div v-if="isOfficeAddin">
        <gaia-button text size="small" @click="router.back()">
          <template #icon>
            <svg
              width="13"
              height="13"
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              stroke-width="1.6"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M9.5 3 4.5 8l5 5M4.5 8h9" />
            </svg>
          </template>
          Back
        </gaia-button>
      </div>

      <!-- The file this history belongs to, then what you can do to it. -->
      <n-flex align="center" :size="14">
        <div class="file-mark">
          <svg
            :width="isOfficeAddin ? 16 : 19"
            :height="isOfficeAddin ? 16 : 19"
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            stroke-width="1.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M3.5 1.8h6l3 3v9.4h-9z" />
            <path d="M9 1.8v3.2h3.2" />
          </svg>
        </div>

        <n-flex vertical :size="3" :style="{ minWidth: 0 }">
          <gaia-header
            :level="1"
            variant="display"
            :size="isOfficeAddin ? 'dialog' : 'page'"
            :style="{ margin: 0 }"
          >
            {{ fileName || '—' }}
          </gaia-header>
          <n-flex align="center" :size="6" :wrap="false">
            <n-icon :component="IconTime" :size="12" :color="subtleColor" />
            <gaia-text size="control" tone="tertiary">
              Version history<template v-if="totalCount">
                · {{ totalCount }} {{ totalCount === 1 ? 'version' : 'versions' }}</template
              >
            </gaia-text>
            <gaia-tag v-if="isDraft" variant="amber" size="small">Draft</gaia-tag>
          </n-flex>
        </n-flex>

        <!-- Compare and upload are hidden in the task pane: both open surfaces
             far wider than it. -->
        <n-flex
          v-if="!isOfficeAddin"
          align="center"
          :size="10"
          :wrap="false"
          :style="{ marginLeft: 'auto' }"
        >
          <gaia-tooltip :disabled="totalCount >= 2">
            <template #trigger>
              <span>
                <gaia-button
                  size="medium"
                  data-tour="vault.compare-button"
                  :disabled="totalCount < 2"
                  @click="showCompareModal = true"
                >
                  <template #icon>
                    <n-icon :component="IconCompare" />
                  </template>
                  Compare versions
                </gaia-button>
              </span>
            </template>
            Two versions are needed before there is anything to compare
          </gaia-tooltip>

          <gaia-button v-if="isDraft" size="medium" @click="continueEditing">
            <template #icon>
              <n-icon :component="IconEdit" />
            </template>
            Continue editing
          </gaia-button>

          <gaia-upload-button
            subject="new version"
            :disabled="writeBlock !== null"
            :title="writeBlock ?? undefined"
            @click="showUploadModal = true"
          />
        </n-flex>
      </n-flex>

      <gaia-data-table
        data-tour="vault.versions-table"
        :columns="tableColumns"
        :action-width="isOfficeAddin ? 46 : 132"
      >
        <template #default="{ rowStyle }">
          <template v-if="loading">
            <gaia-table-skeleton
              :row-style="rowStyle"
              :cells="skeletonCells"
              :rows="4"
              :row-height="52"
            />
          </template>

          <gaia-empty-state v-else-if="!revisions.length" title="No versions yet">
            Every upload of this file is kept here, so you can see what changed and go back to an
            earlier copy.
          </gaia-empty-state>

          <template v-else>
            <template v-for="revision in revisions" :key="revision.uid">
              <gaia-table-row-state
                v-if="confirmRevisionUid === revision.uid"
                variant="confirm"
                :title="`Delete v${revision.version_number} (${revision.file_name})?`"
                @cancel="confirmRevisionUid = null"
                @confirm="confirmDelete(revision)"
              >
                This revision is removed permanently.
              </gaia-table-row-state>

              <div v-else class="row" :style="rowStyle">
                <div class="cell">
                  <span class="version">v{{ revision.version_number }}</span>
                  <gaia-tooltip v-if="revision.uid === currentRevisionUid">
                    <template #trigger><span class="current-dot" /></template>
                    Current version
                  </gaia-tooltip>
                </div>

                <div class="cell name-cell">
                  <n-icon :component="IconDocumentText" :size="14" :color="subtleColor" />
                  <gaia-text
                    truncate
                    size="row"
                    weight="medium"
                    tone="ink"
                    :title="revision.file_name"
                  >
                    {{ revision.file_name }}
                  </gaia-text>
                  <gaia-tag
                    v-if="revision.kind === VaultFileRevisionKind.TEMPLATED"
                    variant="muted"
                    size="small"
                  >
                    Draft
                  </gaia-tag>
                </div>

                <div class="cell">
                  <span class="size">{{ formatBytes(revision.size_b) }}</span>
                </div>

                <div class="cell">
                  <revision-signature-cell
                    :signature-infos="revision.signature_infos ?? null"
                    labeled
                  />
                </div>

                <template v-if="!isOfficeAddin">
                  <div class="cell">
                    <gaia-text
                      v-if="revision.change_description"
                      size="control"
                      tone="text"
                      class="clamp"
                      :title="revision.change_description"
                    >
                      {{ revision.change_description }}
                    </gaia-text>
                    <gaia-text v-else size="control" tone="blank">—</gaia-text>
                  </div>

                  <div class="cell">
                    <n-icon :component="IconCalendar" :size="12" :color="faintColor" />
                    <gaia-text
                      truncate
                      size="control"
                      tone="tertiary"
                      :style="{ fontVariantNumeric: 'tabular-nums' }"
                      :title="formatDateTime(revision.created_at)"
                    >
                      {{ formatDate(revision.created_at) }}
                    </gaia-text>
                  </div>
                </template>

                <!-- The task pane has room for one control, so its actions fold
                     into a menu — and only there does inserting into the open
                     Word document make sense. -->
                <n-flex justify="end" :size="3" :wrap="false">
                  <gaia-dropdown
                    v-if="isOfficeAddin"
                    :options="getOfficeRevisionActionOptions(revision)"
                    :menu-props="getCompactActionsMenuProps"
                    placement="bottom-end"
                    @select="key => handleOfficeRevisionActionSelect(key, revision)"
                  >
                    <gaia-icon-button :dimension="26" quaternary title="More actions">
                      <n-icon :component="IconMore" :size="16" />
                    </gaia-icon-button>
                  </gaia-dropdown>

                  <template v-else>
                    <gaia-icon-button
                      :dimension="28"
                      quaternary
                      title="Download"
                      :loading="downloadingRevisionUid === revision.uid"
                      @click="handleDownload(revision)"
                    >
                      <n-icon :component="IconDownload" :size="14" />
                    </gaia-icon-button>

                    <gaia-tooltip :disabled="canPreview(revision)">
                      <template #trigger>
                        <span>
                          <gaia-icon-button
                            :dimension="28"
                            quaternary
                            title="Preview"
                            :disabled="!canPreview(revision)"
                            @click="openPreview(revision)"
                          >
                            <n-icon :component="IconEye" :size="14" />
                          </gaia-icon-button>
                        </span>
                      </template>
                      Preview is still being prepared
                    </gaia-tooltip>

                    <gaia-icon-button
                      :dimension="28"
                      quaternary
                      :title="writeBlock ?? 'Edit details'"
                      :disabled="writeBlock !== null"
                      @click="openEditModal(revision)"
                    >
                      <n-icon :component="IconEdit" :size="14" />
                    </gaia-icon-button>

                    <gaia-tooltip :disabled="!deleteBlockedReason">
                      <template #trigger>
                        <span>
                          <gaia-icon-button
                            :dimension="28"
                            quaternary
                            class="delete"
                            title="Delete revision"
                            :disabled="!!deleteBlockedReason"
                            @click="askDelete(revision)"
                          >
                            <n-icon :component="IconDelete" :size="14" />
                          </gaia-icon-button>
                        </span>
                      </template>
                      {{ deleteBlockedReason }}
                    </gaia-tooltip>
                  </template>
                </n-flex>
              </div>
            </template>
          </template>
        </template>

        <template v-if="totalCount > 0 && !loading" #footer>
          <gaia-pagination
            :page="currentPage"
            :page-size="pageSize"
            :total="totalCount"
            :page-sizes="[10, 20, 50]"
            @update:page="handlePageChange"
            @update:page-size="handlePageSizeChange"
          />
        </template>
      </gaia-data-table>
    </n-flex>

    <!-- Preview drawer. The three refs are nulled on close so the next open
         mounts a fresh PDF rather than briefly showing the previous revision.
         Flush, so the preview gets the body's height and scrolls its own pages;
         a body that grows with the content leaves the viewer unable to tell
         which page is on screen. -->
    <gaia-drawer
      v-model:show="showPreviewDrawer"
      :width="isOfficeAddin ? 320 : 640"
      title="File preview"
      flush
      @after-leave="
        () => {
          previewRevisionFileUid = null;
          previewRevisionUid = null;
          previewRevisionStatus = null;
        }
      "
    >
      <gaia-vault-file-pdf-preview
        v-if="previewRevisionFileUid"
        :vault-file-uid="previewRevisionFileUid"
        :revision-uid="previewRevisionUid ?? undefined"
        :revision-preview-status="previewRevisionStatus"
      />
    </gaia-drawer>

    <gaia-modal
      :show="showEditModal"
      width="480px"
      @update:show="value => !value && closeEditModal()"
    >
      <template #header>
        <n-flex align="center" :size="11" :wrap="false">
          <div class="file-mark small">
            <n-icon :component="IconEdit" :size="15" />
          </div>
          <n-flex vertical :size="1">
            <gaia-text size="subtitle" weight="semibold" tone="ink">Edit revision</gaia-text>
            <gaia-text v-if="editingRevision" size="meta" tone="subtle">
              v{{ editingRevision.version_number }}
            </gaia-text>
          </n-flex>
        </n-flex>
      </template>

      <n-flex vertical :size="14">
        <gaia-form-field label="File name" required>
          <gaia-input v-model:value="editFileName" placeholder="File name" style="width: 100%" />
        </gaia-form-field>
        <gaia-form-field label="Change description" hint="What changed in this revision?">
          <gaia-textarea
            v-model:value="editDescription"
            placeholder="e.g. Updated clause 4.2, fixed signature block…"
            :autosize="{ minRows: 3, maxRows: 6 }"
            style="width: 100%"
          />
        </gaia-form-field>
      </n-flex>

      <template #footer>
        <n-flex justify="end" :size="8">
          <gaia-button size="small" :disabled="editSaving" @click="closeEditModal"
            >Cancel</gaia-button
          >
          <gaia-button
            type="primary"
            size="small"
            :loading="editSaving"
            :disabled="!editFileName.trim()"
            @click="handleEditSave"
          >
            Save
          </gaia-button>
        </n-flex>
      </template>
    </gaia-modal>

    <vault-file-revision-upload-modal
      v-model:show="showUploadModal"
      :file-uid="uid"
      :file-name="fileName"
      @uploaded="loadData"
    />

    <vault-file-revision-compare-modal
      v-model:show="showCompareModal"
      :file-uid="uid"
      @compare="handleCompare"
    />

    <vault-file-revision-diff-view
      v-model:show="showDiffView"
      :file-uid="uid"
      :revision-a="compareRevisionA"
      :revision-b="compareRevisionB"
    />
  </Layout>
</template>

<style scoped>
/* The file's own mark — an accent-tinted tile, the same idiom the modal header
   uses one size down. */
.file-mark {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: none;
  width: 42px;
  height: 42px;
  border-radius: 11px;
  background: v-bind(markSurface);
  color: v-bind(accentColor);
}

.file-mark.small {
  width: 34px;
  height: 34px;
  border-radius: 9px;
}

.versions-page.office .file-mark {
  width: 32px;
  height: 32px;
  border-radius: 9px;
}

.row {
  display: grid;
  align-items: center;
  gap: 16px;
  min-height: 52px;
  padding: 6px 18px;
  border-bottom: 1px solid v-bind(rowBorder);
}

.row:hover {
  background: v-bind(rowHover);
}

.cell {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}

.name-cell {
  gap: 8px;
  padding-right: 12px;
}

/* A version is a number to compare down the column, so it takes the mono face
   in a bordered pill rather than GaiaTag's chip. */
.version {
  display: inline-flex;
  align-items: center;
  flex: none;
  height: 21px;
  padding: 0 8px;
  border: 1px solid v-bind(pillBorder);
  border-radius: 10px;
  font-family: v-bind(monoFont);
  font-size: v-bind(pillSize);
  font-weight: v-bind(pillWeight);
  color: v-bind(textColor);
}

/* Which revision the file currently points at. A dot rather than a word: the
   column is already narrow and only ever one row carries it. */
.current-dot {
  display: block;
  flex: none;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: v-bind(currentColor);
}

.size {
  display: inline-flex;
  align-items: center;
  height: 21px;
  padding: 0 8px;
  border-radius: 7px;
  background: v-bind(sizeSurface);
  font-size: v-bind(pillSize);
  color: v-bind(softColor);
  font-variant-numeric: tabular-nums;
}

.clamp {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.45;
}

.delete {
  color: v-bind(alertColor);
}

.delete:hover {
  background: v-bind(alertHover);
}
</style>
